import { lazy, Suspense, type ReactNode } from "react";
import { MetricCard } from "../ui/metric-card";
import { ExecutiveSummary } from "./ExecutiveSummary";
import { PlatformLeaderboard } from "./PlatformLeaderboard";
import { SectionHeader } from "./SectionHeader";
import { LandingPageTable } from "./LandingPageTable";
import { CountryTable } from "./CountryTable";
import { ChoroplethSkeleton } from "./ChoroplethSkeleton";
import { SourceMixSection } from "./SourceMixSection";
import { DepthSection } from "./DepthSection";
import { MobileSection } from "./MobileSection";
import { EcommerceSection } from "./EcommerceSection";
import { FrozenInsightsSection } from "./InsightsSection";
import { TimeSeriesSection } from "./TimeSeriesSection";
import { inferGranularity } from "./granularity";
import { engagementRateInfo, keyEventsInfo, revenueInfo, sessionsInfo } from "./metric-info";
import { formatDateRangeLabel } from "../../../shared/report/date-range";
import type { InsightsResult, InsightsV2, ReportPayload } from "../../lib/api";
import { cn } from "../../lib/utils";

const WorldChoropleth = lazy(() => import("./WorldChoropleth"));

interface FrozenReportSectionsProps {
  report: ReportPayload;
  insights: InsightsResult | null;
  insightsV2: InsightsV2 | null;
  /** Label for landing-page exports. */
  propertyLabel: string;
  /** The sample report lists its UTM issues; share links keep them private. */
  showUtmIssues?: boolean;
}

/** The report body for payloads that are already frozen: share links and
 *  the pre-auth sample. No live GA4, no controls. */
export function FrozenReportSections({
  report,
  insights,
  insightsV2,
  propertyLabel,
  showUtmIssues = false,
}: FrozenReportSectionsProps) {
  const { overview, perPlatform, perCountry, landingPages, recommendations, meta, utmHealth } =
    report;
  const showRevenue = overview.totalRevenue > 0;
  const compareActive = meta.compare !== "none";

  return (
    <>
      <section
        className={
          showRevenue
            ? "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
            : "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        }
      >
        <MetricCard
          state="data"
          label="Sessions"
          value={overview.sessions}
          delta={compareActive ? overview.deltas.sessions : null}
          info={sessionsInfo}
        />
        <MetricCard
          state="data"
          label="Engagement rate"
          value={overview.engagementRate}
          format={percent}
          info={engagementRateInfo}
        />
        <MetricCard
          state="data"
          label="Conversions"
          value={overview.keyEvents}
          delta={compareActive ? overview.deltas.keyEvents : null}
          info={keyEventsInfo(meta.propertyId)}
        />
        {showRevenue ? (
          <MetricCard
            state="data"
            label="Revenue"
            value={overview.totalRevenue}
            delta={compareActive ? overview.deltas.totalRevenue : null}
            format={currency}
            info={revenueInfo}
          />
        ) : null}
      </section>

      {/* SKE-3458 — trend chart (TimeSeriesSection) pulled into the
          shared viewer so it mirrors the dashboard's render order
          (KPIs → trend → sourceMix → depth → insights → actions →
          platforms → …). Uses inferred granularity since the shared
          viewer has no granularity control — the owner's window is
          already frozen into the envelope. */}
      {report.trend.length > 0 ? (
        <TimeSeriesSection
          trend={report.trend}
          customConversionsConfigured={overview.customConversions !== undefined}
          showRevenue={showRevenue}
          granularity={inferGranularity(meta.periodStart, meta.periodEnd)}
        />
      ) : null}

      {/* M13.1 — render the frozen sourceMix block when the envelope
          carries one (envelope schemaVersion stays v2 for now; legacy
          v1/v2 shares minted before M13.1 simply omit the block and we
          render nothing). */}
      {report.sourceMix && report.sourceMix.perChannel.length > 0 ? (
        <SourceMixSection
          sourceMix={report.sourceMix}
          compare={meta.compare}
          granularity={inferGranularity(meta.periodStart, meta.periodEnd)}
        />
      ) : null}

      {/* M13.2 — Depth section. Renders for any envelope whose trend
          rows carry the depth metrics (M13.2+ payloads). Pre-M13.2
          envelopes lack pageviews on trend rows; we silently skip the
          section in that case so legacy shares keep rendering. */}
      {report.trend.length > 0 && hasDepthMetrics(report.trend) ? (
        <DepthSection
          trend={report.trend}
          granularity={inferGranularity(meta.periodStart, meta.periodEnd)}
        />
      ) : null}

      {/* SKE-3458 — Frozen AI insights. v4 envelopes carry structured
          `insightsV2` (headline + preview bullets + "See more detail"
          disclosure + action cards — same layout as the dashboard).
          v3 envelopes fall back to legacy paragraph + bullets. v1/v2
          and failure-at-mint cases render nothing. No regenerate
          button on the public surface. */}
      <FrozenInsightsSection
        insights={insights}
        insightsV2={insightsV2}
        startDate={meta.periodStart}
        endDate={meta.periodEnd}
      />

      <ReportSection eyebrow="What to do next" title="Important actions to consider">
        <ExecutiveSummary recommendations={recommendations} />
      </ReportSection>

      <ReportSection id="platforms" title="Where social traffic is coming from">
        <PlatformLeaderboard
          rows={perPlatform}
          compare={meta.compare}
          showRevenue={showRevenue}
          propertyId={meta.propertyId}
          ecommercePerPlatform={
            report.ecommerce?.windowActive ? report.ecommerce.perPlatform : undefined
          }
          currencyCode={meta.currencyCode ?? null}
        />
      </ReportSection>

      {/* M13.3 — Mobile section. Renders only when the envelope carries
          a mobile block (M13.3+ shares). Pre-M13.3 envelopes silently
          skip — same graceful-degradation pattern depth uses. */}
      {report.mobile && report.mobile.perDevice.length > 0 ? (
        <MobileSection
          mobile={report.mobile}
          granularity={inferGranularity(meta.periodStart, meta.periodEnd)}
          showRevenue={showRevenue}
          currencyCode={meta.currencyCode}
        />
      ) : null}

      <ReportSection id="landing-pages" title="Pages winning the click">
        <LandingPageTable
          rows={landingPages}
          propertyLabel={propertyLabel}
          periodLabel={formatDateRangeLabel(meta.periodStart, meta.periodEnd)}
          propertyId={meta.propertyId}
        />
      </ReportSection>

      {/* M13.4 — Ecommerce section. Renders frozen in the share envelope
          when the originating build had probe-pass + windowActive.
          Pre-M13.4 envelopes silently skip — same graceful-degradation
          pattern as the M13.1/2/3 sections. */}
      {report.ecommerce && report.ecommerce.windowActive ? (
        <EcommerceSection
          ecommerce={report.ecommerce}
          currencyCode={meta.currencyCode ?? null}
          granularity={inferGranularity(meta.periodStart, meta.periodEnd)}
          compareActive={meta.compare !== "none"}
        />
      ) : null}

      <ReportSection id="geography" title="Where in the world">
        {perCountry.length === 0 ? (
          <CountryTable rows={perCountry} propertyId={meta.propertyId} />
        ) : (
          <div className="space-y-4">
            <Suspense fallback={<ChoroplethSkeleton />}>
              <WorldChoropleth rows={perCountry} />
            </Suspense>
            <CountryTable rows={perCountry} propertyId={meta.propertyId} />
          </div>
        )}
      </ReportSection>

      {hasUtmHealthTotals(utmHealth) ? (
        <ReportSection id="utm-health" title="How well-tagged the traffic is">
          <UtmHealthSummary score={utmHealth} showIssues={showUtmIssues} />
        </ReportSection>
      ) : null}
    </>
  );
}

// M15 — frozen envelopes come straight off R2 and are rendered verbatim;
// a blob that predates (or mis-shapes) the current scorer contract can
// arrive without `utmHealth.totals` (seen live 2026-06-10: Bugsnag
// "Cannot read properties of undefined (reading 'rows')"). Same sniffing
// pattern as hasDepthMetrics below: render the section only when the
// block is structurally complete, so a partial envelope drops one card
// instead of taking the whole viewer down. The cast is deliberate — the
// TypeScript shape says totals is always there; the JSON boundary says
// otherwise.
function hasUtmHealthTotals(utmHealth: ReportPayload["utmHealth"]): boolean {
  const t = (utmHealth as { totals?: { rows?: unknown; sessions?: unknown } } | undefined)?.totals;
  return typeof t?.rows === "number" && typeof t?.sessions === "number";
}

interface ReportSectionProps {
  id?: string;
  eyebrow?: string;
  title: ReactNode;
  children: ReactNode;
}

function ReportSection({ id, eyebrow, title, children }: ReportSectionProps) {
  return (
    <section id={id} className="scroll-mt-24 space-y-4">
      <SectionHeader eyebrow={eyebrow} title={title} />
      {children}
    </section>
  );
}

function UtmHealthSummary({
  score,
  showIssues,
}: {
  score: ReportPayload["utmHealth"];
  showIssues: boolean;
}) {
  const bandBar: Record<ReportPayload["utmHealth"]["band"], string> = {
    excellent: "bg-green-500",
    good: "bg-green-400",
    "needs-work": "bg-yellow-500",
    broken: "bg-red-500",
  };
  const bandChip: Record<ReportPayload["utmHealth"]["band"], string> = {
    excellent: "bg-green-50 text-green-700",
    good: "bg-green-50 text-green-700",
    "needs-work": "bg-yellow-50 text-yellow-700",
    broken: "bg-red-50 text-red-700",
  };
  const bandLabel: Record<ReportPayload["utmHealth"]["band"], string> = {
    excellent: "Excellent",
    good: "Good",
    "needs-work": "Needs work",
    broken: "Broken",
  };
  return (
    <div className="rounded-2xl bg-white p-5 ring-1 ring-neutral-950/5 sm:p-7">
      <div className="flex flex-wrap items-baseline justify-between gap-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-neutral-500">
            {score.totals.rows.toLocaleString()} tagged URLs ·{" "}
            {score.totals.sessions.toLocaleString()} sessions
          </p>
          <span
            className={cn(
              "mt-2 inline-flex rounded-full px-2.5 py-1.5 text-[13px] font-bold",
              bandChip[score.band],
            )}
          >
            {bandLabel[score.band]}
          </span>
        </div>
        <p className="font-display text-[64px] font-[900] leading-none tracking-[-0.03em] text-neutral-950 tabular-nums">
          {score.score}
          <span className="ml-1 text-2xl font-semibold text-neutral-400">/100</span>
        </p>
      </div>
      <div aria-hidden="true" className="mt-5 h-2 overflow-hidden rounded-full bg-neutral-100">
        <div
          className={cn("h-full rounded-full w-(--progress)", bandBar[score.band])}
          style={{ "--progress": `${score.score}%` } as React.CSSProperties}
        />
      </div>
      {showIssues && score.issues.length > 0 ? (
        <ul role="list" className="mt-6 divide-y divide-neutral-100 border-t border-neutral-100">
          {score.issues.map((issue) => (
            <li key={`${issue.category}-${issue.label}`} className="flex items-start gap-3 py-4">
              <span
                aria-hidden="true"
                className={cn(
                  "mt-1.5 size-2 shrink-0 rounded-full",
                  issue.severity === "severe" ? "bg-red-500" : "bg-yellow-500",
                )}
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">{issue.label}</p>
                <p className="mt-1 text-sm leading-[1.5] text-neutral-600 text-pretty">
                  {issue.detail}
                </p>
                <p className="mt-1.5 text-xs text-neutral-500 tabular-nums">
                  {issue.sessionCount.toLocaleString()} sessions affected
                </p>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function percent(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function currency(n: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

// M13.2 — pre-M13.2 share envelopes don't carry pageviews on trend rows.
// Sniff the first row to decide whether to render the DepthSection so
// legacy shares stay clean. Numeric `0` is a valid value (zero traffic
// in window) so we test for `typeof === "number"` rather than truthiness.
function hasDepthMetrics(trend: ReportPayload["trend"]): boolean {
  const first = trend[0];
  if (!first) return false;
  return (
    typeof first.pageviews === "number" &&
    typeof first.activeUsers === "number" &&
    typeof first.totalUsers === "number"
  );
}
